/**
 * Click handler for map marker overlays.
 *
 * Encapsulates the desktop/mobile tap-to-navigate behaviour so that
 * markers.plugin stays free of navigation, environment-reading, and
 * Redux dispatch concerns.
 *
 * Desktop: opens the listing URL immediately on click.
 * Mobile:  first tap selects the marker (showing the carousel); second tap
 *          on the same marker opens the URL.
 *
 * Injected into markers.plugin via the onMarkerClick option.
 *
 * @module searchResultsMapVariant/handlers/markerClickHandler
 */

import mapInteractionSlice from '../redux/interactionSlice';
import { selectSelectedMarker } from '../redux/selectors';

const { actions } = mapInteractionSlice;

/**
 * Creates the marker click handler.
 *
 * @param {Function} dispatch - Redux store dispatch function.
 * @param {Function} isDesktop - Returns true when the viewport is desktop.
 * @param {Function} getState - Returns the current Redux store state.
 * @returns {Function} Handler called with marker data on click.
 */
export default function createMarkerClickHandler(dispatch, isDesktop, getState) {
  return function handleMarkerClick(markerData, event) {
    const interactionType = event?.type === 'dblclick' ? 'double_click' : 'slow_double_click';
    const lastSelectedMarker = selectSelectedMarker(getState());
    const urlOpened = isDesktop() ||
      (lastSelectedMarker?.id === markerData.id && lastSelectedMarker?.type === markerData.type);

    document.body.dispatchEvent(
      new CustomEvent('markerPinClicked', {
        detail: {
          marker: markerData,
          interactionType,
          urlOpened,
        }
      })
    );

    if (isDesktop()) {
      window.open(markerData.url, '_blank');
      return;
    }

    // Mobile: second tap on the same marker opens the URL.
    if (lastSelectedMarker?.id === markerData.id && lastSelectedMarker?.type === markerData.type) {
      window.open(markerData.url, '_blank');
      return;
    }

    dispatch(actions.setSelectedMarker(markerData));
  };
}
